import { z } from 'zod';

import {
  accessIdentifierSchema,
  accessPasswordSchema,
  assertAccessOrdinal,
  sitePrefixFor,
} from './site-prefix';

/**
 * SFTP-Konten: Namen, Schlüssel und die Regeln, die sie sicher machen.
 *
 * ## Warum diese Datei aussieht wie `database.ts`
 *
 * Weil die Bedrohung dieselbe ist. Auf einem Worker teilen sich alle Kunden
 * **einen** SFTP-Dienst, und er ist über die Edge aus dem Internet erreichbar.
 * Die einzige Trennung zwischen zwei Kunden ist das Home-Verzeichnis, in das
 * SFTPGo einen Benutzer einsperrt. Fällt sie, sieht ein Kunde die Dateien aller
 * anderen auf demselben Server.
 *
 * Deshalb dieselben drei Bremsen wie bei den Datenbanken:
 *
 * 1. Der **Zeichenvorrat** eines Benutzernamens ist `[a-z0-9]`
 *    (`site-prefix.ts`). Ein Name kann damit weder einen Pfad verlassen noch in
 *    einer Filterregel etwas anderes bedeuten, als er aussieht.
 * 2. Der **Präfix** bindet jeden Namen an genau eine Site. Master und Agent
 *    rechnen ihn beide aus der Site-ID aus; er wird nie übertragen.
 * 3. Ein **Integrationstest mit zwei Sites gegeneinander** prüft die Trennung
 *    gegen einen echten SFTPGo, statt sie zu behaupten.
 *
 * ## Was hier bewusst nicht steht
 *
 * Das Home-Verzeichnis. Der Master beschreibt den Zugang, der **Agent**
 * entscheidet, wo das auf seiner Platte liegt — dieselbe Regel wie für
 * Einhängequellen. Er setzt es auf `<siteRoot>/public` und ausdrücklich nicht
 * auf `<siteRoot>`: Dort liegt seit M4a `.dumps/` mit den Sicherungen der
 * Kunden-Datenbanken, und die gehören nicht in den Dateibrowser des Kunden.
 */

// -- Namensbildung -----------------------------------------------------------

/** Höchste laufende Nummer je Site. Begrenzt zugleich die Länge des Namens. */
export const MAX_SFTP_ORDINAL = 99;

/**
 * Wie viele SFTP-Konten eine Site haben darf.
 *
 * Fünf, wie bei den Datenbanken: genug für den Kunden, seine Agentur und eine
 * Übergabe, und wenig genug, dass eine durchgereichte Kontoliste überschaubar
 * bleibt.
 */
export const MAX_SITE_SFTP_ACCOUNTS = 5;

/**
 * Benutzername des `n`-ten SFTP-Kontos einer Site, z. B. `ks04e87b83c1d2s1`.
 *
 * Das `s` unterscheidet ihn von Datenbank (`d`) und Datenbankbenutzer (`u`).
 * Beide Zugangsarten teilen einen Namensraum, weil sie einen Präfix teilen —
 * ohne diesen Buchstaben wäre `…1` mehrdeutig.
 */
export function buildSftpUsername(siteId: string, ordinal: number): string {
  return accessIdentifierSchema.parse(
    `${sitePrefixFor(siteId)}s${String(assertAccessOrdinal(ordinal, MAX_SFTP_ORDINAL, 'eines SFTP-Kontos'))}`,
  );
}

// -- Öffentliche Schlüssel ---------------------------------------------------

/** Wie viele hinterlegte Schlüssel ein Konto tragen darf. */
export const MAX_SFTP_PUBLIC_KEYS = 5;

/**
 * Ein öffentlicher SSH-Schlüssel im `authorized_keys`-Format.
 *
 * Eng gefasst, obwohl der Wert „nur" als JSON zu SFTPGo geht: Er stammt vom
 * Kunden und ist damit die **einzige** Zeichenkette dieses Meilensteins, die
 * nicht aus unserer eigenen Erzeugung kommt. Erlaubt sind die drei Typen, die
 * OpenSSH heute noch ohne Warnung annimmt, gefolgt von Base64 und einem
 * optionalen Kommentar — keine Optionsliste am Zeilenanfang
 * (`command=`, `environment=`), denn die wäre bei einem echten OpenSSH eine
 * Befehlsausführung.
 */
export const SFTP_PUBLIC_KEY_PATTERN =
  /^(?:ssh-ed25519|ssh-rsa|ecdsa-sha2-nistp(?:256|384|521)) [A-Za-z\d+/]+={0,3}(?: [\w.@ -]{1,128})?$/;

export const sftpPublicKeySchema = z
  .string()
  .max(4096, { message: 'Der Schlüssel ist zu lang.' })
  .regex(SFTP_PUBLIC_KEY_PATTERN, {
    message:
      'Muss ein öffentlicher SSH-Schlüssel sein, z. B. "ssh-ed25519 AAAAC3Nz… kunde@rechner". Erzeugen mit: ssh-keygen -t ed25519. Optionen am Zeilenanfang sind nicht zugelassen.',
  });

// -- Das Konto in der Spezifikation ------------------------------------------

/**
 * Ein SFTP-Konto, wie es in der `SiteSpec` steht.
 *
 * Das Passwort steht hier im Klartext, aus demselben Grund wie bei den
 * Datenbanken: Die Spezifikation ist alles, was der Agent braucht — er stellt
 * keine Rückfragen und holt sich nichts nach. Getragen wird das an denselben
 * drei Stellen: `site.spec` liegt beim Master verschlüsselt, die Queue
 * transportiert nur eine Operations-ID, und die Log-Redaction schwärzt `spec`
 * als Ganzes.
 */
export const siteSftpSpecSchema = z.object({
  username: accessIdentifierSchema,
  password: accessPasswordSchema,
  publicKeys: z.array(sftpPublicKeySchema).max(MAX_SFTP_PUBLIC_KEYS).default([]),
});

export type SiteSftpSpec = z.infer<typeof siteSftpSpecSchema>;

// -- Außensicht --------------------------------------------------------------

/**
 * Was der Betreiber über ein SFTP-Konto zu sehen bekommt.
 *
 * **Ohne Passwort.** Es steht genau einmal in der Antwort auf das Anlegen
 * beziehungsweise auf die Rotation (`SiteSftpCredentials`) und danach nie
 * wieder — dieselbe Haltung wie beim Bootstrap-Token und bei den Datenbanken.
 */
export const siteSftpViewSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  username: accessIdentifierSchema,
  publicKeys: z.array(sftpPublicKeySchema),
  /**
   * Fährt der Worker bereits eine Spezifikation, die dieses Konto nennt?
   *
   * `false` unmittelbar nach dem Anlegen ist der Normalfall und kein Fehler —
   * der Reconciler wendet beim nächsten Durchlauf an.
   */
  liveOnNode: z.boolean(),
  createdAt: z.string(),
});

export type SiteSftpView = z.infer<typeof siteSftpViewSchema>;

/**
 * Die vollständigen Zugangsdaten — der einzige Weg, auf dem ein SFTP-Passwort
 * den Master verlässt.
 *
 * `host` ist ein **Name** und niemals die Adresse eines Workers: Der Kunde
 * verbindet sich über die Edge, und welcher Worker seine Site fährt, geht ihn
 * nichts an. `port` unterscheidet die Worker — er ist der einzige Teil der
 * Zugangsdaten, der sich bei einem Umzug ändert.
 */
export const siteSftpCredentialsSchema = siteSftpViewSchema.extend({
  password: accessPasswordSchema,
  host: z.string(),
  port: z.number().int(),
});

export type SiteSftpCredentials = z.infer<typeof siteSftpCredentialsSchema>;
